import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import './AdminDashboard.css';

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Check if user is admin
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }

    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors

      const response = await api.get('/api/admin/stats');

      // Debug: Log the response for troubleshooting
      console.log('Admin stats API response:', response);

      // Set stats from response
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);

      // Handle different types of errors
      if (err.response) {
        // Server responded with an error status
        const status = err.response.status;
        const errorMessage = err.response.data?.error;

        if (status === 403) {
          setError(t('admin.dashboard.errors.accessDenied'));
        } else if (status === 401) {
          setError(t('admin.dashboard.errors.authFailed'));
        } else if (status === 404) {
          setError(t('admin.dashboard.errors.userNotFound'));
        } else {
          setError(errorMessage || t('admin.dashboard.errors.serverError'));
        }
      } else if (err.request) {
        // Request was made but no response received
        setError(t('admin.dashboard.errors.connectionError'));
      } else {
        // Something else happened
        setError(err.message || t('admin.dashboard.errors.unexpectedError'));
      }

      setStats(null); // Reset stats on error
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">{t('admin.dashboard.loadingStats')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error">{error}</div>
        <button onClick={fetchStats} style={{ marginTop: '10px' }}>{t('admin.dashboard.retry')}</button>
      </div>
    );
  }

  // Ensure stats is loaded and has expected structure
  if (!stats || !stats.users || !stats.properties || !stats.bookings || !stats.revenue || !stats.messages || !stats.reviews) {
    return (
      <div className="admin-dashboard">
        <div className="error">{t('admin.dashboard.failedToLoad')}</div>
        <button onClick={fetchStats} style={{ marginTop: '10px' }}>{t('admin.dashboard.retry')}</button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>{t('admin.dashboard.title')}</h1>
        <p>{t('admin.dashboard.subtitle')}</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{t('admin.dashboard.users')}</h3>
            <div className="stat-number">{stats.users?.total || 0}</div>
            <div className="stat-details">
              <span>{stats.users?.hosts || 0} {t('admin.dashboard.hosts')}</span>
              <span>{stats.users?.recent || 0} {t('admin.dashboard.new30d')}</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🏠</div>
          <div className="stat-content">
            <h3>{t('admin.dashboard.properties')}</h3>
            <div className="stat-number">{stats.properties?.total || 0}</div>
            <div className="stat-details">
              <span>{stats.properties?.active || 0} {t('admin.dashboard.active')}</span>
              <span>{stats.properties?.recent || 0} {t('admin.dashboard.new30d')}</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <h3>{t('admin.dashboard.bookings')}</h3>
            <div className="stat-number">{stats.bookings?.total || 0}</div>
            <div className="stat-details">
              <span>{stats.bookings?.confirmed || 0} {t('admin.dashboard.confirmed')}</span>
              <span>{stats.bookings?.pending || 0} {t('admin.dashboard.pending')}</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>{t('admin.dashboard.revenue')}</h3>
            <div className="stat-number">R$ {(stats.revenue?.total || 0).toFixed(2)}</div>
            <div className="stat-details">
              <span>{t('admin.dashboard.totalConfirmed')}</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💬</div>
          <div className="stat-content">
            <h3>{t('admin.dashboard.messages')}</h3>
            <div className="stat-number">{stats.messages?.total || 0}</div>
            <div className="stat-details">
              <span>{stats.messages?.unread || 0} {t('admin.dashboard.unread')}</span>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>{t('admin.dashboard.reviews')}</h3>
            <div className="stat-number">{stats.reviews?.total || 0}</div>
            <div className="stat-details">
              <span>{t('admin.dashboard.avgRating')}: {(stats.reviews?.average_rating || 0).toFixed(1)} ⭐</span>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-actions">
        <h2>{t('admin.dashboard.management')}</h2>
        <div className="action-cards">
          <Link to="/admin/properties" className="action-card">
            <div className="action-icon">🏠</div>
            <h3>{t('admin.dashboard.properties')}</h3>
            <p>{t('admin.dashboard.manageProperties')}</p>
          </Link>

          <Link to="/admin/bookings" className="action-card">
            <div className="action-icon">📅</div>
            <h3>{t('admin.dashboard.bookings')}</h3>
            <p>{t('admin.dashboard.viewManageBookings')}</p>
          </Link>

          <Link to="/admin/users" className="action-card">
            <div className="action-icon">👥</div>
            <h3>{t('admin.dashboard.users')}</h3>
            <p>{t('admin.dashboard.manageUsers')}</p>
          </Link>

          <Link to="/admin/messages" className="action-card">
            <div className="action-icon">💬</div>
            <h3>{t('admin.dashboard.messages')}</h3>
            <p>{t('admin.dashboard.viewConversations')}</p>
          </Link>

          <Link to="/admin/revenue" className="action-card">
            <div className="action-icon">💰</div>
            <h3>{t('admin.dashboard.revenue')}</h3>
            <p>{t('admin.dashboard.trackEarnings')}</p>
          </Link>

          <Link to="/admin/reviews" className="action-card">
            <div className="action-icon">⭐</div>
            <h3>{t('admin.dashboard.reviews')}</h3>
            <p>{t('admin.dashboard.viewManageReviews')}</p>
          </Link>

          <Link to="/admin/api-tokens" className="action-card">
            <div className="action-icon">🔑</div>
            <h3>API Tokens</h3>
            <p>Manage API access tokens for external integrations</p>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
